/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  ChangeEvent,
  CSSProperties,
  MouseEvent,
  useEffect,
  useState,
} from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { BaseOptionType } from 'antd/es/select';
import { AxiosError } from 'axios';
import {
  resetGroups,
  resetTeachers,
  searchGroups,
  searchTeachers,
  setSchedulePublicRequestTeacherId,
  setSchedulePublicRequestText,
  setSchedulePublicSearchText,
} from 'entities/index';
import { IGroup, ITeacher } from 'entities/types';
import { useInvisibleCaptcha } from 'features/yaSmartCaptcha';
import { SCHEDULE_PAGE } from 'shared/consts';
import { useHistoryOfSearch, useLastSearchQuery } from 'shared/hooks';
import { useDebounce } from 'shared/hooks/useDebounce/useDebounce';
import {
  formatFullnameToUppercase,
  isNonEmptyStr,
  isPositiveIntStr,
  useAppDispatch,
  useAppSelector,
} from 'shared/lib';

import {
  isGroup,
  makeHistoryOptionLabel,
  makeOptionLabel,
  makeOptionStyle,
} from '../lib';
import { searchType } from '../models/types';

export const useSearchSchedule = (searchType: searchType) => {
  const [options, setOptions] = useState<BaseOptionType[]>([]);

  const navigate = useNavigate();
  const { searchText } = useAppSelector((state) => state.schedulePublicReducer);
  const { groups, groupsIsLoading } = useAppSelector(
    (state) => state.groupsReducer,
  );
  const { teachers, teachersIsLoading } = useAppSelector(
    (state) => state.teachersReducer,
  );

  const { deleteCurrentHistory, filterHistory, setSearchHistory } =
    useHistoryOfSearch();

  const { setLastSearchQuery } = useLastSearchQuery();

  const { token } = useInvisibleCaptcha();

  const dispatch = useAppDispatch();

  const debouncedSearchText = useDebounce(searchText, 500);

  useEffect(() => {
    if (debouncedSearchText) searchQuery(debouncedSearchText);
  }, [debouncedSearchText]);

  const handleOptionClearClick = (
    e: MouseEvent<HTMLButtonElement>,
    value: string,
    teacherId?: string,
  ) => {
    e.stopPropagation();
    deleteCurrentHistory(value, teacherId);
    setOptions(makeOptions());
  };

  const makeOptions = (data?: IGroup[] | ITeacher[]): BaseOptionType[] => {
    const options: BaseOptionType[] = [];
    const historyOptionsData = filterHistory(searchText || '');
    const regex = new RegExp(/^[A-Za-zА-ЯЁа-яё\s]+;\d+$/);
    if (historyOptionsData.length) {
      const style: CSSProperties = makeOptionStyle(searchType);
      historyOptionsData.forEach((item: string) => {
        let value;
        let label;
        if (regex.test(item)) {
          const items = item.split(';');
          value = `h_${items[0]}`;
          label = makeHistoryOptionLabel(
            searchType,
            items[0],
            handleOptionClearClick,
            items[1],
          );
          options.push({
            value,
            label,
            style,
            teacherId: items[1],
          });
        } else {
          value = `h_${item}`;
          label = makeHistoryOptionLabel(
            searchType,
            item,
            handleOptionClearClick,
          );
          options.push({ value, label, style });
        }
      });
    }

    if (data)
      data.forEach((item: IGroup | ITeacher) => {
        const value = isGroup(item)
          ? (item as IGroup).groupNum
          : (item as ITeacher).fio;
        const label = makeOptionLabel(searchType, item, value);
        const style: CSSProperties = makeOptionStyle(searchType);

        if (!isGroup(item)) {
          options.push({
            value,
            label,
            style,
            teacherId: (item as ITeacher).id,
          });
        } else {
          options.push({
            value,
            label,
            style,
          });
        }
      });
    return options;
  };

  const searchQuery = async (data: string) => {
    try {
      if (isNonEmptyStr(data)) {
        const trimmed = data.trim();
        if (token) {
          if (isPositiveIntStr(trimmed)) {
            dispatch(resetTeachers());
            await dispatch(searchGroups({ data: trimmed })).unwrap();
          } else {
            dispatch(resetGroups());
            await dispatch(searchTeachers({ data: trimmed })).unwrap();
          }
        }
      } else {
        setOptions([]);
      }
    } catch (error) {
      let errorMessage = 'Ошибка при выполнении поиска';
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          console.error(error);
          errorMessage = '';
        } else {
          errorMessage = error.message;
        }
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      if (errorMessage) toast.error(errorMessage);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const data = e.target.value;
    dispatch(setSchedulePublicSearchText(data));
  };

  const handleFocus = () => {
    setOptions([]);
    if (searchType === 'main') searchQuery(searchText ?? '');
  };

  const handleSelect = (value: unknown, option: BaseOptionType) => {
    const clearedValue = (value as string).replace(/h_/g, '');

    dispatch(
      setSchedulePublicSearchText(formatFullnameToUppercase(clearedValue)),
    );
    dispatch(setSchedulePublicRequestText(clearedValue));
    dispatch(setSchedulePublicRequestTeacherId(option.teacherId as string));
    if (clearedValue) {
      if (option.teacherId) {
        setSearchHistory(
          `${clearedValue as string};${option.teacherId as string}`,
        );
        setLastSearchQuery(
          `${formatFullnameToUppercase(clearedValue as string)};${option.teacherId as string}`,
        );
      } else {
        setSearchHistory(clearedValue as string);
        setLastSearchQuery(clearedValue as string);
      }
    }

    if (searchType === 'main') {
      dispatch(resetTeachers());
      dispatch(resetGroups());
      setOptions([]);
      navigate(SCHEDULE_PAGE);
    }
  };

  const handleInputClearClick = () => {
    dispatch(setSchedulePublicSearchText(''));
    setOptions(makeOptions());
  };

  return {
    handleChange,
    handleFocus,
    handleSelect,
    handleInputClearClick,
    options,
    groups,
    teachers,
    searchText,
    groupsIsLoading,
    teachersIsLoading,
    searchQuery,
    setOptions,
    makeOptions,
  };
};
